import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import pluralize from 'pluralize';

import capitalize from 'common/util/capitalize';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import {
  formatPrice,
  numberWithCommas,
} from 'common/formatters';

import {
  getHouseholdPets,
} from 'common/householdHelpers';

import {
  hasRolePartner,
  hasRoleAdmin,
} from 'common/userRoles';

import {
  isApplicationPastStatus,
} from 'common/householdApplication/applicationHelpers';

import {
  PET_OPTION_VALUE_DOG,
} from 'common/petOptionItems';

import {
  HOUSEHOLD_APPLICATION_STATUS_PENDING_VERIFICATION,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_TRANSUNION,
} from 'common/householdConstants';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrConcessionTag from 'client/components/SrConcessionTag';

const SR_HOUSEHOLD_PREQUAL_REQUIREMENTS_LIST = [
  'creditScore',
  'monthlyIncome',
  'pendingVerification',
  'felonies',
  'bankruptcies',
  'evictions',
  'pets',
];

function getCreditScoreText({
  isGuarantor,
  isQualified,
  inProgress,
  tenantAlertReport,
  hideRenterInfo,
}) {
  if (hideRenterInfo) {
    return !tenantAlertReport && inProgress
      ? (isGuarantor ? 'Pending verification...' : 'Pending...')
      : (isQualified ? 'Verified' : 'Unqualified');
  }

  return !tenantAlertReport && inProgress
    ? 'Pending...'
    : (tenantAlertReport?.creditScore || 'None');
}

function SrTextNote({ children }) {
  return (
    <SrText
      display='block'
      fontSize='12px'
      weight='500'
      lineHeight='20px'
      font='greycliff'
      color='blueGreycliff'
    >
      {children}
    </SrText>
  );
}

function getMonthlyIncomeText({
  monthlyIncome,
  isQualified,
  inProgress,
  hideRenterInfo,
}) {
  if (hideRenterInfo) {
    return inProgress
      ? 'Pending...'
      : (isQualified ? 'Verified' : 'Unqualified');
  }

  return monthlyIncome ? formatPrice(monthlyIncome) : 'None';
}

function SrHouseholdRequirement({ id, householdListing, householdRequirements, children, hideNotes }) {
  const mistmatchReason = householdListing[`mismatch${capitalize(id)}Reason`];

  return (
    <SrFlex className='SrHouseholdRequirement' align='center'>
      <SrBox marginBottom='auto'>
        <div
          className={classnames('icon', {
            isQualified: householdRequirements[id]?.isQualified,
            inProgress: householdRequirements[id]?.inProgress,
          })}
        />
      </SrBox>
      <SrBox>
        {children}
        {!hideNotes && mistmatchReason && (
          <SrBox marginTop='4px' marginBottom='2px'>
            <SrTextNote>
              {mistmatchReason}
            </SrTextNote>
          </SrBox>
        )}
      </SrBox>
    </SrFlex>
  );
}

class SrHouseholdPrequalSummary extends Component {
  static propTypes = {
    partner: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    listing: PropTypes.object.isRequired,
    householdListing: PropTypes.object.isRequired,
    tenantScreeningCriteria: PropTypes.object.isRequired,
    rentersQualifications: PropTypes.array.isRequired,
    householdRequirements: PropTypes.object.isRequired,
    rentersVerifications: PropTypes.array.isRequired,
    guarantorsVerifications: PropTypes.array.isRequired,
    householdVerifications: PropTypes.object.isRequired,
    householdApplications: PropTypes.array.isRequired,
    guarantorApplications: PropTypes.array.isRequired,
    tenantAlertReports: PropTypes.array.isRequired,
    displayTitle: PropTypes.bool,
    householdAccepted: PropTypes.bool,
  };

  render() {
    const {
      partner,
      currentUser,
      listing,
      householdListing,
      concession,
      tenantScreeningCriteria,
      rentersQualifications,
      householdRequirements,
      rentersVerifications,
      guarantorsVerifications,
      householdVerifications,
      householdApplications,
      guarantorApplications,
      tenantAlertReports,
      displayTitle,
      householdAccepted,
    } = this.props;

    const {
      monthlyIncome,
      moveInDate,
      bankruptcies,
      evictions,
      creditScore,
    } = householdRequirements;

    const maximumPetsAllowed = listing.maximumPetsAllowed || 0;

    const { pets, petsByType } = getHouseholdPets({
      householdApplications: householdApplications,
    });

    const dogs = pets.filter((pet) => pet.type === PET_OPTION_VALUE_DOG);

    const tenantAlertReportByUserId = tenantAlertReports.reduce((memo, tenantAlertReport) => ({
      ...memo,
      [tenantAlertReport.userId]: tenantAlertReport,
    }), {});

    const leaseTerm = (householdApplications[0] || {}).leaseTerm;

    const householdApplicationByUserId = householdApplications.reduce((memo, householdApplication) => ({
      ...memo,
      [householdApplication.userId]: householdApplication,
    }), {});

    const rentersVerificationsByUserId = rentersVerifications.reduce((memo, renterVerification) => ({
      ...memo,
      [renterVerification.userId]: renterVerification,
    }), {});
    const guarantorsVerificationsByUserId = guarantorsVerifications.reduce((memo, guarantorVerification) => ({
      ...memo,
      [guarantorVerification.userId]: guarantorVerification,
    }), {});

    const applicationsPassPendingVerification = householdApplications
      .every((householdApplication) => {
        return isApplicationPastStatus(householdApplication, HOUSEHOLD_APPLICATION_STATUS_PENDING_VERIFICATION); // eslint-disable-line max-len
      });

    const applicationsPassPendingTransunion = householdApplications
      .every((householdApplication) => {
        return isApplicationPastStatus(householdApplication, HOUSEHOLD_APPLICATION_STATUS_PENDING_TRANSUNION);
      });

    const nextHouseholdRequirementIdInProgress = SR_HOUSEHOLD_PREQUAL_REQUIREMENTS_LIST
      .find((requirementId) => {
        if (householdRequirements[requirementId]) {
          return householdRequirements[requirementId].inProgress;
        }

        if (
          requirementId === 'pendingVerification'
          && (applicationsPassPendingVerification && !applicationsPassPendingTransunion)
        ) {
          return true;
        }

        return false;
      });

    const hideRenterInfo = (
      partner.renterDocsPublic !== true
      && !(hasRolePartner(currentUser.roles) && hasRoleAdmin(currentUser.roles))
    );

    const checkIfAllPetsAllowed = () => {
      const petsNotAllowed = pets.filter((pet) => {
        const petWeightAllowed = isNaN(listing.petWeight) || (
          pet.isEsa
          || listing.petWeight >= parseInt(pet.weight, 10)
        );
        return !(pets.length <= maximumPetsAllowed && petWeightAllowed || pet.isEsa);
      });

      return petsNotAllowed.length === 0;
    };

    let creditScoreDOM = null;

    if (guarantorApplications.length > 0) {
      creditScoreDOM = (
        <>
          {guarantorApplications.map((guarantorApplication, index) => {
            const tenantAlertReport = tenantAlertReportByUserId[guarantorApplication.userId];
            const isQualified = tenantAlertReport && tenantAlertReport.creditScore >= tenantScreeningCriteria.creditScore; // eslint-disable-line max-len

            return (
              <SrText
                key={guarantorApplication.userId}
                color={isQualified ? 'green' : 'red'}
                font='greycliff'
                weight='800'
                fontSize='16px'
                lineHeight='19px'
              >
                {getCreditScoreText({
                  isGuarantor: true,
                  isQualified,
                  inProgress: creditScore.inProgress,
                  tenantAlertReport,
                  hideRenterInfo,
                })}
                {(index + 1) < guarantorApplications.length ? ', ' : ''}
              </SrText>
            );
          })}
          <SrText
            color={!householdRequirements.creditScore.isQualified
              ? 'red'
              : 'green'
            }
            font='greycliff'
            weight='800'
            fontSize='16px'
            lineHeight='19px'
          >
            {` (${pluralize('Guarantor', guarantorApplications.length)})`}
          </SrText>
          {rentersQualifications.map((({ userId }, index) => { // eslint-disable-line max-len
            const tenantAlertReport = tenantAlertReportByUserId[userId];
            const isQualified = tenantAlertReport && tenantAlertReport.creditScore >= tenantScreeningCriteria.creditScore; // eslint-disable-line max-len

            return (
              <SrBox key={userId}>
                <SrTextNote>
                  {`Renter ${index + 1} : `}
                  {getCreditScoreText({
                    isQualified,
                    inProgress: creditScore.inProgress,
                    tenantAlertReport,
                    hideRenterInfo,
                  })}
                </SrTextNote>
              </SrBox>
            );
          }))}
        </>
      );
    }
    else {
      creditScoreDOM = (
        <>
          {!creditScore.inProgress && rentersQualifications.map((({ userId }, index) => { // eslint-disable-line max-len
            const tenantAlertReport = tenantAlertReportByUserId[userId];
            const isQualified = tenantAlertReport && tenantAlertReport.creditScore >= tenantScreeningCriteria.creditScore; // eslint-disable-line max-len

            return (
              <SrText
                key={userId}
                color={isQualified ? 'green' : 'red'}
                font='greycliff'
                weight='800'
                fontSize='16px'
                lineHeight='19px'
              >
                {getCreditScoreText({
                  isQualified,
                  inProgress: creditScore.inProgress,
                  tenantAlertReport,
                  hideRenterInfo,
                })}
                {(index + 1) < rentersQualifications.length ? ', ' : ''}
              </SrText>
            );
          }))}
          {creditScore.inProgress && (
            <>
              <SrText>Pending credit check...</SrText>
              <SrBox marginTop='4px' marginBottom='2px'>
                {nextHouseholdRequirementIdInProgress === 'creditScore' && rentersQualifications.map((({ userId }, index) => { // eslint-disable-line max-len
                  const tenantAlertReport = tenantAlertReportByUserId[userId];
                  const isQualified = tenantAlertReport && tenantAlertReport.creditScore >= tenantScreeningCriteria.creditScore; // eslint-disable-line max-len

                  return (
                    <SrBox key={userId}>
                      <SrTextNote>
                        {`Renter ${index + 1} : `}
                        {getCreditScoreText({
                          isQualified,
                          inProgress: creditScore.inProgress,
                          tenantAlertReport,
                          hideRenterInfo,
                        })}
                      </SrTextNote>
                    </SrBox>
                  );
                }))}
              </SrBox>
            </>
          )}
        </>
      );
    }

    return (
      <SrBox className='SrHouseholdPrequalSummary'>
        <SrBox marginBottom='18px'>
          {displayTitle && (
            <SrText
              color='blueGreycliff'
              font='greycliff'
              weight='800'
              fontSize='16px'
              lineHeight='25px'
            >
              Summary
            </SrText>
          )}
          <SrTextNote>
            Any criminal history is reported in the documents below
          </SrTextNote>
          {householdListing.appProNote && (
            <SrTextNote>
              {householdListing.appProNote}
            </SrTextNote>
          )}
        </SrBox>
        <SrBox className='qualificationList'>
          <SrFlex className='SrHouseholdRequirement'>
            <SrFlex align='center'>
              <SrBox marginBottom='auto'>
                <div className='icon isQualified' />
              </SrBox>
              <SrBox>
                <SrText
                  color='green'
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Rental Rate:&nbsp;
                </SrText>
                <SrText
                  color='green'
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  ${numberWithCommas(
                    householdAccepted
                      ? householdListing.rentPrice || listing.price
                      : listing.price
                  )}
                </SrText>
              </SrBox>
            </SrFlex>
            {!!concession && (
              <SrFlex
                height='22px'
                marginLeft='8px'
              >
                <SrConcessionTag
                  concession={concession}
                  rentPrice={listing.price}
                />
              </SrFlex>
            )}
          </SrFlex>
          <SrHouseholdRequirement
            id='moveInDate'
            householdListing={householdListing}
            householdRequirements={householdRequirements}
            hideNotes={hideRenterInfo}
          >
            <SrText
              color={moveInDate.isQualified ? 'green' : 'yellowGold'}
              font='greycliff'
              weight='800'
              fontSize='16px'
              lineHeight='19px'
            >
              {moveInDate.hasLeaseStartDate ? 'Lease Start Date:' : 'Move-in Date:'}
              &nbsp;
            </SrText>
            <SrText
              color={moveInDate.isQualified ? 'green' : 'yellowGold'}
              font='greycliff'
              weight='800'
              fontSize='16px'
              lineHeight='19px'
            >
              {momentWithAustinTimezone(moveInDate.value).format('MMM D')}
              &nbsp;
            </SrText>
          </SrHouseholdRequirement>

          {leaseTerm && (
            <SrFlex className='SrHouseholdRequirement' align='center'>
              <SrBox marginBottom='auto'>
                <div className='icon isQualified' />
              </SrBox>
              <SrBox>
                <SrText
                  color='green'
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Lease Term:&nbsp;
                </SrText>
                <SrText
                  color='green'
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {leaseTerm} Months
                </SrText>
              </SrBox>
            </SrFlex>
          )}

          <SrHouseholdRequirement
            id='pets'
            householdListing={householdListing}
            householdRequirements={householdRequirements}
            hideNotes={hideRenterInfo}
          >
            <SrText
              color='green'
              font='greycliff'
              weight='800'
              fontSize='16px'
              lineHeight='19px'
            >
              {`${pluralize('Pet', pets.length)}: `}
              {Object.keys(petsByType).map((petType, petTypeIndex) => {
                const pets = petsByType[petType];
                let dogDetailsDOM = null;
                if (petType.includes(PET_OPTION_VALUE_DOG)) {
                  dogDetailsDOM = (
                    <SrText
                      color={checkIfAllPetsAllowed() ? 'green' : 'red'}
                      font='greycliff'
                      weight='800'
                      fontSize='16px'
                      lineHeight='19px'
                    >
                      {'('}
                      {pets.map((pet, index) => {
                        const petWeightAllowed = isNaN(listing.petWeight) || (
                          pet.isEsa
                          || listing.petWeight >= parseInt(pet.weight, 10)
                        );
                        return (
                          <SrText
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            color={(pets.length <= maximumPetsAllowed && petWeightAllowed) || pet.isEsa
                              ? 'green'
                              : 'red'}
                            font='greycliff'
                            weight='800'
                            fontSize='16px'
                            lineHeight='19px'
                          >
                            {pet.weight} lbs
                            {(index + 1) < pets.length ? ', ' : ''}
                          </SrText>
                        );
                      })}
                      {')'}
                    </SrText>
                  );
                }

                return (
                  <SrText
                    key={petType}
                    color={pets.filter((pet) => !pet.isEsa).length <= maximumPetsAllowed ? 'green' : 'red'}
                    font='greycliff'
                    weight='800'
                    fontSize='16px'
                    lineHeight='19px'
                  >
                    {`${pets.length} ${pluralize(petType, pets.length)} `}
                    {dogDetailsDOM}
                    {(petTypeIndex + 1) < Object.keys(petsByType).length ? ', ' : ''}
                  </SrText>
                );
              })}
              {pets.length === 0 && (
                <SrText
                  color='green'
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  No pets
                </SrText>
              )}
            </SrText>
          </SrHouseholdRequirement>

          {listing.petsAllowed && tenantScreeningCriteria.breedRestrictions.length > 0 && dogs.length > 0 && (
            <SrHouseholdRequirement
              id='petBreeds'
              householdListing={householdListing}
              householdRequirements={householdRequirements}
              hideNotes={hideRenterInfo}
            >
              <SrText
                color='green'
                font='greycliff'
                weight='800'
                fontSize='16px'
                lineHeight='19px'
              >
                {`Dog Breed${dogs.length > 0 ? ': ' : ''}`}
              </SrText>
              {dogs.length > 0 && (
                dogs.map((dog, index) => {
                  const isQualified = !tenantScreeningCriteria
                    .breedRestrictions.find(({ name }) => name === dog.breed) || dog.isEsa;
                  return (
                    <SrText
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      color={isQualified ? 'green' : 'red'}
                      font='greycliff'
                      weight='800'
                      fontSize='16px'
                      lineHeight='19px'
                    >
                      {dog.breed}
                      {(index + 1) < dogs.length ? ', ' : ''}
                    </SrText>
                  );
                })
              )}
            </SrHouseholdRequirement>
          )}
          {householdVerifications.petScreening &&
          <SrFlex className='SrHouseholdRequirement' align='center'>
            <SrBox marginBottom='auto'>
              <div className={classnames('icon', {
                isQualified: !householdVerifications.petScreening?.inProgress,
                inProgress: householdVerifications.petScreening?.inProgress,
              })}
              />
            </SrBox>
            <SrBox>
              <SrText
                weight='800'
                color={!householdVerifications.petScreening?.inProgress ? 'green' : 'yellowGold'}
                font='greycliff'
                fontSize='16px'
                lineHeight='19px'
              >
                Pet Screening:&nbsp;
              </SrText>
              <SrText
                color={!householdVerifications.petScreening?.inProgress ? 'green' : 'yellowGold'}
                weight='800'
                font='greycliff'
                fontSize='16px'
                lineHeight='19px'
              >
                {!householdVerifications.petScreening?.inProgress ? 'Verified' : 'Pending verification...'}
              </SrText>
            </SrBox>
          </SrFlex>
          }

          <SrHouseholdRequirement
            id='evictions'
            householdListing={householdListing}
            householdRequirements={householdRequirements}
            hideNotes={hideRenterInfo}
          >
            {!householdRequirements.evictions.inProgress && (
              <SrText
                color={evictions.isQualified ? 'green' : 'red'}
                font='greycliff'
                weight='800'
                fontSize='16px'
                lineHeight='19px'
              >
                {evictions.isQualified ? 'No evictions ' : 'Eviction '}
                {(tenantScreeningCriteria.evictionsAccepted
                  && tenantScreeningCriteria.evictionsDescription) || ''}
              </SrText>
            )}
            {householdRequirements.evictions.inProgress && (
              <SrText
                color='yellowGold'
                font='greycliff'
                weight='800'
                fontSize='16px'
                lineHeight='19px'
              >
                Evictions: Pending background check
              </SrText>
            )}
            {householdRequirements.evictions.inProgress && nextHouseholdRequirementIdInProgress === 'evictions' && rentersQualifications.map(({ userId, qualifications }, index) => { // eslint-disable-line max-len
              return (
                <div key={userId}>
                  <SrTextNote>
                    {`Renter ${index + 1}`}: {
                      qualifications.evictions.inProgress ? 'Pending...' : 'Completed'
                    }
                  </SrTextNote>
                </div>
              );
            })}
          </SrHouseholdRequirement>
          {householdRequirements.bankruptcies && (
            <SrHouseholdRequirement
              id='bankruptcies'
              householdListing={householdListing}
              householdRequirements={householdRequirements}
              hideNotes={hideRenterInfo}
            >
              {!householdRequirements.bankruptcies?.inProgress && (
                <SrText
                  color={bankruptcies.isQualified ? 'green' : 'red'}
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  {bankruptcies.isQualified ? 'No bankruptcies ' : 'Bankruptcy '}
                  {(tenantScreeningCriteria.bankruptciesAccepted
       && tenantScreeningCriteria.bankruptciesDescription) || ''}
                </SrText>
              )}
              {householdRequirements.bankruptcies?.inProgress && (
                <SrText
                  color='yellowGold'
                  font='greycliff'
                  weight='800'
                  fontSize='16px'
                  lineHeight='19px'
                >
                  Bankruptcies: Pending background check
                </SrText>
              )}
              {householdRequirements.bankruptcies?.inProgress && nextHouseholdRequirementIdInProgress === 'bankruptcies' && rentersQualifications.map(({ userId, qualifications }, index) => { // eslint-disable-line max-len
                return (
                  <div key={userId}>
                    <SrTextNote>
                      {`Renter ${index + 1}`}: {
                        qualifications.bankruptcies.inProgress ? 'Pending...' : 'Completed'
                      }
                    </SrTextNote>
                  </div>
                );
              })}
            </SrHouseholdRequirement>
          )}
          <SrHouseholdRequirement
            id='creditScore'
            householdListing={householdListing}
            householdRequirements={householdRequirements}
            hideNotes={hideRenterInfo}
          >
            <SrText
              color={creditScore.inProgress ? 'yellowGold' : creditScore.isQualified ? 'green' : 'red'}
              font='greycliff'
              weight='800'
              fontSize='16px'
              lineHeight='19px'
            >
              Credit scores:&nbsp;
            </SrText>
            {creditScoreDOM}
          </SrHouseholdRequirement>

          <SrHouseholdRequirement
            id='monthlyIncome'
            householdListing={householdListing}
            householdRequirements={householdRequirements}
            hideNotes={hideRenterInfo}
          >
            <SrText
              weight='800'
              color={monthlyIncome.inProgress ? 'yellowGold' : monthlyIncome.isQualified ? 'green' : 'red'}
              font='greycliff'
              fontSize='16px'
              lineHeight='19px'
            >
              Monthly Income:&nbsp;
            </SrText>
            {!monthlyIncome.inProgress && (
              <SrText
                color={monthlyIncome.isQualified ? 'green' : 'red'}
                weight='800'
                font='greycliff'
                fontSize='16px'
                lineHeight='19px'
              >
                {hideRenterInfo
                  ? monthlyIncome.isQualified ? 'Verified' : 'Unqualified'
                  : `${formatPrice(monthlyIncome.value)} (${(monthlyIncome.value / listing.price).toFixed(1)}x Rent)` // eslint-disable-line max-len
                }
              </SrText>
            )}
            {monthlyIncome.inProgress && (
              <SrText
                weight='800'
                font='greycliff'
                fontSize='16px'
                lineHeight='19px'
                color='yellowGold'
              >
                {hideRenterInfo
                  ? 'Pending verification…'
                  : `Pending proof of ${formatPrice(monthlyIncome.value)}`
                }
              </SrText>
            )}
            {guarantorApplications.map((guarantorApplication, index) => {
              return (
                <SrBox key={guarantorApplication.id}>
                  <SrTextNote>
                    {`Guarantor ${index + 1}`}: {getMonthlyIncomeText({
                      monthlyIncome: (
                        guarantorApplication.incomeVerification?.incomeMonthly
                        || guarantorApplication.monthlyIncome
                      ),
                      isQualified: guarantorApplication.monthlyIncome >= listing.price * tenantScreeningCriteria.rentToIncomeRatio, // eslint-disable-line max-len
                      inProgress: monthlyIncome.inProgress,
                      hideRenterInfo,
                    })}
                  </SrTextNote>
                </SrBox>
              );
            })}
            {guarantorApplications.length === 0 && rentersQualifications.map((({ userId }, index) => {
              const householdApplication = householdApplicationByUserId[userId];

              return (
                <SrBox key={userId}>
                  <SrTextNote>
                    {`Renter ${index + 1}`}: {getMonthlyIncomeText({
                      monthlyIncome: householdApplication.monthlyIncome,
                      isQualified: monthlyIncome.isQualified,
                      inProgress: monthlyIncome.inProgress,
                      hideRenterInfo,
                    })}
                  </SrTextNote>
                </SrBox>
              );
            }))}
          </SrHouseholdRequirement>

          <SrFlex className='SrHouseholdRequirement' align='center'>
            <SrBox marginBottom='auto'>
              <div className={classnames('icon', {
                isQualified: !householdVerifications.rental.inProgress,
                inProgress: householdVerifications.rental.inProgress,
              })}
              />
            </SrBox>
            <SrBox>
              <SrText
                weight='800'
                color={!householdVerifications.rental.inProgress ? 'green' : 'yellowGold'}
                font='greycliff'
                fontSize='16px'
                lineHeight='19px'
              >
                Rental History:&nbsp;
              </SrText>
              <SrText
                color={!householdVerifications.rental.inProgress ? 'green' : 'yellowGold'}
                weight='800'
                font='greycliff'
                fontSize='16px'
                lineHeight='19px'
              >
                {!householdVerifications.rental.inProgress ? 'Verified' : 'Pending verification...'}
              </SrText>
              {householdVerifications.rental.inProgress && rentersQualifications.map(({ userId }, index) => { // eslint-disable-line max-len
                return (
                  <div key={userId}>
                    <SrTextNote>
                      {`Renter ${index + 1}`}: {
                        !rentersVerificationsByUserId[userId]?.rental?.inProgress ? 'Completed' : 'Pending...'
                      }
                    </SrTextNote>
                  </div>
                );
              })}
            </SrBox>
          </SrFlex>

          <SrFlex className='SrHouseholdRequirement' align='center'>
            <SrBox marginBottom='auto'>
              <div className={classnames('icon', {
                isQualified: !householdVerifications.employment.inProgress,
                inProgress: householdVerifications.employment.inProgress,
              })}
              />
            </SrBox>
            <SrBox>
              <SrText
                weight='800'
                color={!householdVerifications.employment.inProgress ? 'green' : 'yellowGold'}
                font='greycliff'
                fontSize='16px'
                lineHeight='19px'
              >
                Employment:&nbsp;
              </SrText>
              <SrText
                color={!householdVerifications.employment.inProgress ? 'green' : 'yellowGold'}
                weight='800'
                font='greycliff'
                fontSize='16px'
                lineHeight='19px'
              >
                {!householdVerifications.employment.inProgress ? 'Verified' : 'Pending verification...'}
              </SrText>
              {householdVerifications.employment.inProgress && guarantorApplications.length === 0 && rentersQualifications.map(({ userId }, index) => { // eslint-disable-line max-len
                return (
                  <div key={userId}>
                    <SrTextNote>
                      {`Renter ${index + 1}`}: {
                        !rentersVerificationsByUserId[userId]?.employment?.inProgress ? 'Completed' : 'Pending...' // eslint-disable-line max-len
                      }
                    </SrTextNote>
                  </div>
                );
              })}
              {householdVerifications.employment.inProgress && guarantorApplications.map(({ userId }, index) => { // eslint-disable-line max-len
                return (
                  <div key={userId}>
                    <SrTextNote>
                      {`Guarantor ${index + 1}`}: {
                        !guarantorsVerificationsByUserId[userId]?.employment?.inProgress ? 'Completed' : 'Pending...' // eslint-disable-line max-len
                      }
                    </SrTextNote>
                  </div>
                );
              })}
            </SrBox>
          </SrFlex>
        </SrBox>
      </SrBox>
    );
  }
}

SrHouseholdPrequalSummary.defaultProps = {
  displayTitle: true,
};

SrHouseholdPrequalSummary.displayName = 'SrHouseholdPrequalSummary';

export default SrHouseholdPrequalSummary;
